import React, { useState, useEffect } from 'react'
import { 
  Avatar, 
  Typography, 
  Button, 
  message
} from 'antd'
import { 
  UserOutlined,
  RightOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { User } from '../types'
import { wechatLogin } from '../utils/wechat'
import { authAPI, userAPI } from '../services/api'
import './DashboardPage.css'

const { Title, Text } = Typography

const DashboardPage: React.FC = () => {
  const { user, logout, updateUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [userInfo, setUserInfo] = useState<User | null>(user)

  useEffect(() => {
    setUserInfo(user)
    
    // 检查用户是否已选择经销商（已有关联经销商则不强制跳转）
    const checkDealerSelection = () => {
      const hasDealer = user?.dealer_id != null && user.dealer_id !== '' && user.dealer_id !== 0
      if (user && !hasDealer) {
        console.log('🔍 用户未选择经销商，强制跳转到经销商选择页面')
        message.info('请先选择您的所属经销商')
        navigate('/dealer-selection', { 
          state: { 
            returnTo: '/dashboard',
            forceSelection: true // 标记为强制选择
          } 
        })
        return
      }
    }
    
    // 检查是否从经销商选择页面返回
    const state = location.state as any
    if (state?.dealerUpdated) {
      console.log('✅ 经销商已更新，刷新用户信息')
      // 从后端获取最新的用户信息
      const fetchLatestUserInfo = async () => {
        try {
          const response = await userAPI.getCurrentUser() as any
          if (response.success) {
            const data = response.data || {}
            // is_sync_wecom 以服务端为准；头像仅在后端未返回时沿用本地
            const merged = {
              ...data,
              avatar_url: data.avatar_url || user?.avatar_url || '',
              is_sync_wecom: Boolean(data.is_sync_wecom),
            }
            console.log('🔄 获取到最新用户信息:', merged)
            updateUser(merged)
            setUserInfo(merged)
          }
        } catch (error) {
          console.error('❌ 获取最新用户信息失败:', error)
        }
      }
      fetchLatestUserInfo()
      // 如果是从经销商选择页面返回，就不需要再检查经销商选择了
      return
    }
    
    // 检查经销商选择
    checkDealerSelection()
    
    // 静默访问后端health端口，帮助函数计算预启动
    const preWarmBackend = async () => {
      try {
        await authAPI.healthCheck()
        console.log('✅ 后端预启动完成')
      } catch (error) {
        // 静默处理错误，不显示任何反馈
        console.log('⚠️ 后端预启动失败，但不影响用户体验')
      }
    }
    
    // 立即执行预启动
    preWarmBackend()
    
    // 检测企业微信环境并添加CSS类
    const isWeChatWork = /wxwork/i.test(navigator.userAgent) || /MicroMessenger/i.test(navigator.userAgent)
    if (isWeChatWork) {
      document.documentElement.classList.add('wechat-browser')
      
      // 强制修复企业微信内嵌浏览器的样式问题
      const style = document.createElement('style')
      style.textContent = `
        .mobile-header {
          padding: 0 20px !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        .header-content {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          justify-content: flex-end !important;
          display: flex !important;
        }
        .logout-btn {
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          min-width: auto !important;
          height: auto !important;
          line-height: 1 !important;
          color: #003A96 !important;
          font-size: 16px !important;
        }
        .mobile-content {
          padding: 72px 20px 80px 20px !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        .mobile-dashboard {
          width: 100% !important;
          background-attachment: fixed !important;
        }
      `
      document.head.appendChild(style)
    }
  }, [user, location.state, navigate])

  const handleLogout = () => {
    if (window.confirm('您确定要退出登录吗？')) {
      logout()
      message.success('已退出登录')
      // 退出登录后使用硬跳转确保页面完全刷新
      setTimeout(() => {
        console.log('🚀 退出登录，跳转到登录页')
        window.location.href = '/login'
      }, 500)
    }
  }


  return (
    <div className="mobile-dashboard">
      {/* 移动端顶部导航 */}
      <div className="mobile-header">
        <div className="header-content">
          <Button
            type="text"
            onClick={handleLogout}
            className="logout-btn"
          >
            登出→
          </Button>
        </div>
      </div>

      {/* 移动端内容区域 */}
      <div className="mobile-content">
        {/* 欢迎语 */}
          <div className="welcome-section">
            <Title level={2} className="welcome-title">
              欢迎使用<br />别克精灵！
            </Title>
          </div>

        {/* 内容容器 */}
        <div className="content-container">
          {/* 用户信息卡片 */}
          <div 
            className="user-card"
            onClick={() => {
              navigate('/user-edit')
            }}
          >
            <div className="user-profile">
              <Avatar
                size={60}
                src={wechatLogin.getAvatarUrl(userInfo?.avatar_url)}
                icon={<UserOutlined />}
                className="user-avatar"
              />
              <div className="user-info">
                <div className="user-name">{userInfo?.consultant_name}</div>
                <div className="user-role">别克顾问</div>
              </div>
              <div className="user-action">
                <RightOutlined className="action-arrow" />
              </div>
            </div>
          </div>

          {/* 菜单区域 */}
          <div className="menu-container">
            {/* 上半部分：创建邀请函 */}
            <div className="menu-section">
              <div className="menu-row">
                <Button 
                  type="primary" 
                  size="large"
                  className="menu-btn"
                  onClick={() => {
                    navigate('/test-drive-invitation')
                  }}
                >
                  <div className="btn-content">
                    <div className="btn-icon">
                      <PlusOutlined />
                    </div>
                    <div className="btn-text">
                      <div className="btn-subtitle">创建</div>
                      <div className="btn-title">试乘试驾<br />邀请函</div>
                    </div>
                  </div>
                </Button>
                <Button 
                  type="primary" 
                  size="large"
                  className="menu-btn"
                  onClick={() => {
                    navigate('/delivery-service-invitation')
                  }}
                >
                  <div className="btn-content">
                    <div className="btn-icon">
                      <PlusOutlined />
                    </div>
                    <div className="btn-text">
                      <div className="btn-subtitle">创建</div>
                      <div className="btn-title">交车服务<br />邀请函</div>
                    </div>
                  </div>
                </Button>
              </div>
            </div>
            {/* 下半部分：回执查看按钮 */}
            <div className="menu-section">
              <Button 
                type="default" 
                size="large"
                block
                className="menu-btn-bottom"
                onClick={() => {
                  navigate('/receipt-view')
                }}
              >
                回执查看
              </Button>
            </div>
          </div>
        </div>
      </div>


      {/* 移动端底部 */}
      <div className="mobile-footer">
        <Text type="secondary">别克精灵 ©2025</Text>
      </div>
    </div>
  )
}

export default DashboardPage